/**
 * The footer: what a host may do to this attempt, laid out the way the provider says.
 *
 * One component for every provider. The spec chooses which controls appear, in which zone and under
 * which label; the rules for when each is shown or enabled come from the session (closed, replaying,
 * scoring), decided by the player and handed in as state. A provider that needs a different rule
 * gets it as data, never as a second footer.
 *
 * Element ids and classes are the ones the page has always had, so the stylesheet and the
 * end-to-end suite address the same footer whichever provider built it.
 */

import type { PagePayload, Score, Tool } from '@cet/asset-contract';

import type { FooterControl, FooterLabel, FooterSpec } from './providers/index.js';

export interface FooterActions {
  reset(): void;
  check(): void;
  submit(): void;
  again(): void;
  back(): void;
  next(): void;
  finish(): void;
  /** One of the asset's tools was pressed (D98). */
  tool(id: string): void;
}

/** Everything the footer shows, decided by the player from the session; nothing is read here. */
export interface FooterState {
  /** Which language the labels speak. */
  readonly locale: string;
  /** Which way the activity's pages read: Previous and Next follow it. */
  readonly dir: 'rtl' | 'ltr';
  /** An asset is mounted and connected. */
  readonly live: boolean;
  /** Review or solution: there is nothing to answer, so nothing to reset or start again. */
  readonly replay: boolean;
  /** The attempt accepts no more answers. */
  readonly closed: boolean;
  readonly submitted: boolean;
  readonly scoring: boolean;
  /** The host holds the grading rules, so Submit can score. */
  readonly gradable: boolean;
  /** Why Submit is off, when the asset declares nothing to grade. */
  readonly submitTitle: string;
  /** The badge: a score, marks withheld, or nothing. */
  readonly result: { readonly kind: 'score'; readonly total: Score } | { readonly kind: 'withheld' } | null;
  readonly page: PagePayload | null;
  /** The provider's navigation: under `linear`, Next waits for the asset's `canAdvance`. */
  readonly navigation: 'free' | 'linear';
  readonly status: string;
  /** Check: not there at all (closed, an exam), or there and pressable now or not. */
  readonly check: { readonly hidden: boolean; readonly enabled: boolean };
  /** The asset's tools for the bar (D98); empty when it sent none. */
  readonly tools: readonly Tool[];
  /** The provider's finish button: which words, and whether it may be pressed now. */
  readonly finish: { readonly label: 'finish' | 'checkMe'; readonly enabled: boolean } | null;
}

export interface Footer {
  render(state: FooterState): void;
  /** Ask before something that cannot be undone. Resolves true for the first button. */
  confirm(text: { message: string; ok: string; cancel: string }): Promise<boolean>;
}

const ENGLISH: Record<FooterLabel, string> = {
  reset: 'Reset',
  check: 'Check',
  submit: 'Submit',
  again: 'Start a new attempt',
  back: 'Previous page',
  next: 'Next page',
  nextWords: 'Next',
  of: 'of',
  finish: "I'm done",
  checkMe: 'Check me',
};

const ICON = {
  reset: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"></path><path d="M3 3v5h5"></path>',
  check: '<path d="M20 6 9 17l-5-5"></path>',
  back: '<path d="m15 18-6-6 6-6"></path>',
  next: '<path d="m9 18 6-6-6-6"></path>',
};

/** A tool's icon by its name (`TOOL_ICONS`): the player's own drawing, never the asset's. */
const TOOL_ICON: Record<string, string> = {
  words: '<path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path>',
  ai: '<path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0l1.58 6.14a2 2 0 0 0 1.44 1.44l6.14 1.58a.5.5 0 0 1 0 .96l-6.14 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z"></path>',
  listening: '<path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"></path>',
  reading: '<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path>',
  writing: '<path d="M21.17 6.81a1 1 0 0 0-3.99-3.99L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z"></path>',
  speaking: '<path d="M12 19v3"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><rect x="9" y="2" width="6" height="13" rx="3"></rect>',
  vocabulary: '<path d="M4 7V4h16v3"></path><path d="M9 20h6"></path><path d="M12 4v16"></path>',
  grammar: '<path d="m6 16 6-12 6 12"></path><path d="M8 12h8"></path><path d="m16 20 2 2 4-4"></path>',
};

function icon(paths: string): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = paths;
  return svg;
}

/** A button whose words can change with the locale, beside an optional icon that does not. */
function button(id: string, className: string, paths?: string): { element: HTMLButtonElement; words: Text } {
  const element = document.createElement('button');
  element.type = 'button';
  element.id = id;
  element.className = className;
  if (paths !== undefined) element.append(icon(paths));
  const words = document.createTextNode('');
  element.append(words);
  return { element, words };
}

const text = (value: string): Text => document.createTextNode(value);
const tagged = (tag: 'strong' | 'span', value: string): HTMLElement => {
  const element = document.createElement(tag);
  element.textContent = value;
  return element;
};

export function createFooter(container: HTMLElement, spec: FooterSpec, actions: FooterActions): Footer {
  const label = (key: FooterLabel, locale: string): string =>
    spec.labels?.[key]?.[locale] ?? spec.labels?.[key]?.['en'] ?? ENGLISH[key];

  const reset = button('reset', 'ghost', ICON.reset);
  const check = button('check', 'secondary', ICON.check);
  const submit = button('submit', 'primary-outline');
  const again = button('again', 'secondary');
  const finish = button('finish', 'finish');
  const result = Object.assign(document.createElement('div'), { id: 'result', className: 'result' });
  const status = Object.assign(document.createElement('span'), { id: 'status', className: 'status' });
  status.setAttribute('aria-live', 'polite');

  const back = button('back', 'round', ICON.back).element;
  const nextButton = button('next', spec.nextWords === true ? 'next-words primary-round' : 'round primary-round', ICON.next);
  const next = nextButton.element;
  const pageLabel = Object.assign(document.createElement('div'), { id: 'page-label', className: 'page-label' });
  const pageDots = Object.assign(document.createElement('div'), { id: 'page-dots', className: 'page-dots' });
  pageDots.setAttribute('aria-hidden', 'true');
  const where = Object.assign(document.createElement('div'), { className: 'pager-where' });
  where.append(pageLabel, pageDots);
  const pager = Object.assign(document.createElement('nav'), { id: 'pager', className: 'pager' });
  pager.setAttribute('aria-label', 'Pages');
  pager.hidden = true;
  pager.append(back, where, next);

  const tools = Object.assign(document.createElement('div'), { id: 'bar-tools', className: 'bar-tools' });
  tools.setAttribute('role', 'group');
  tools.setAttribute('aria-label', 'Tools');
  // Redrawn only when the asset sends a different list, so a button keeps its focus between renders.
  let toolsDrawn = '';

  // Each control is made once, whether or not this spec places it, so `render` has one shape.
  const controls: Record<FooterControl, HTMLElement> = {
    reset: reset.element,
    check: check.element,
    result,
    status,
    submit: submit.element,
    again: again.element,
    finish: finish.element,
    pager,
    tools,
  };

  const zone = (className: string, members: readonly FooterControl[]): HTMLDivElement => {
    const element = document.createElement('div');
    element.className = `zone ${className}`;
    element.append(...members.map((member) => controls[member]));
    return element;
  };
  // An empty middle zone keeps the grid's three columns, so start and end stay where they were.
  container.replaceChildren(zone('start', spec.start), zone('middle', spec.middle), zone('end', spec.end));

  reset.element.addEventListener('click', actions.reset);
  check.element.addEventListener('click', actions.check);
  submit.element.addEventListener('click', actions.submit);
  again.element.addEventListener('click', actions.again);
  finish.element.addEventListener('click', actions.finish);
  back.addEventListener('click', actions.back);
  next.addEventListener('click', actions.next);

  // ---------------------------------------------------------------- confirm

  const dialog = Object.assign(document.createElement('dialog'), { id: 'confirm', className: 'confirm' });
  const message = document.createElement('p');
  const ok = Object.assign(document.createElement('button'), { type: 'button', className: 'finish' });
  const cancel = Object.assign(document.createElement('button'), { type: 'button', className: 'secondary' });
  const buttons = Object.assign(document.createElement('div'), { className: 'confirm-actions' });
  buttons.append(cancel, ok);
  dialog.append(message, buttons);
  document.body.append(dialog);

  return {
    render(state) {
      const { locale } = state;
      reset.words.data = label('reset', locale);
      check.words.data = label('check', locale);
      submit.words.data = label('submit', locale);
      again.words.data = label('again', locale);
      pager.dir = state.dir;
      back.setAttribute('aria-label', label('back', locale));
      // With words, they are its name; an arrow alone needs one.
      if (spec.nextWords === true) nextButton.words.data = label('nextWords', locale);
      else next.setAttribute('aria-label', label('next', locale));

      reset.element.hidden = state.replay || state.submitted;
      reset.element.disabled = !state.live;
      check.element.hidden = state.check.hidden;
      check.element.disabled = !state.check.enabled;
      submit.element.hidden = state.closed;
      submit.element.disabled = !state.live || !state.gradable || state.scoring;
      submit.element.title = state.submitTitle;
      again.element.hidden = !state.submitted || state.replay;
      status.textContent = state.status;

      finish.element.hidden = state.finish === null;
      if (state.finish !== null) {
        finish.words.data = label(state.finish.label, locale);
        finish.element.disabled = !state.finish.enabled;
      }

      result.hidden = state.result === null;
      if (state.result?.kind === 'score') {
        const { total } = state.result;
        const outcome = total.passed === undefined ? '' : total.passed ? ' · passed' : ' · failed';
        result.replaceChildren(
          tagged('strong', `${total.raw}/${total.max}`),
          text(`${Math.round(total.scaled * 100)}%${outcome}`),
        );
        result.dataset['outcome'] = total.passed === undefined ? '' : total.passed ? 'passed' : 'failed';
      } else if (state.result?.kind === 'withheld') {
        result.replaceChildren(tagged('strong', 'Submitted'), text('marks withheld'));
        result.dataset['outcome'] = '';
      }

      renderPager(state);
      renderTools(state.tools);
    },

    confirm({ message: words, ok: yes, cancel: no }) {
      message.textContent = words;
      ok.textContent = yes;
      cancel.textContent = no;
      dialog.dir = /[֐-׿؀-ۿ]/.test(words) ? 'rtl' : 'ltr';
      return new Promise((resolve) => {
        const close = (answer: boolean): void => {
          ok.onclick = null;
          cancel.onclick = null;
          dialog.oncancel = null;
          dialog.close();
          resolve(answer);
        };
        ok.onclick = () => close(true);
        cancel.onclick = () => close(false);
        // Escape is "no", never "yes".
        dialog.oncancel = (event) => {
          event.preventDefault();
          close(false);
        };
        dialog.showModal();
        cancel.focus();
      });
    },
  };

  /** The asset's tools (D98): a button each, or a label that is only read; its own icon by name. */
  function renderTools(list: readonly Tool[]): void {
    tools.hidden = list.length === 0;
    const drawn = JSON.stringify(list);
    if (drawn === toolsDrawn) return;
    toolsDrawn = drawn;
    tools.replaceChildren(
      ...list.map((tool) => {
        const element = document.createElement(tool.kind === 'label' ? 'span' : 'button');
        element.className = tool.kind === 'label' ? 'bar-tool bar-label' : 'bar-tool';
        element.dataset['tool'] = tool.id;
        const paths = tool.icon === undefined ? undefined : TOOL_ICON[tool.icon];
        if (paths !== undefined) element.append(icon(paths));
        const words = tagged('span', tool.label);
        words.className = 'bar-tool-words';
        element.append(words);
        if (tool.badge !== undefined) element.append(tagged('span', ` · ${tool.badge}`));
        if (element instanceof HTMLButtonElement) {
          element.type = 'button';
          // Its name whatever a narrow bar leaves of its words.
          element.setAttribute('aria-label', tool.badge === undefined ? tool.label : `${tool.label} · ${tool.badge}`);
          element.addEventListener('click', () => actions.tool(tool.id));
        }
        return element;
      }),
    );
  }

  /**
   * Previous, where you are, next. Nothing at all for one page, or for an asset that never reported
   * pages — it then keeps its own controls. Under `linear` navigation, Forward also waits for the
   * asset's own rule (`canAdvance`); under `free` it does not.
   */
  function renderPager({ page, live, locale, navigation }: FooterState): void {
    pager.hidden = page === null || page.count < 2;
    if (page === null || page.count < 2) return;

    const of = label('of', locale);
    pageLabel.replaceChildren(text(`${page.index + 1} `), tagged('span', `${of} ${page.count}`));
    // "1 מתוך 14" read in a left-to-right footer comes out as "מתוך 14 1"; the phrase has its own
    // direction — the words', not the locale's: a provider with no Hebrew label says "of" in English.
    pageLabel.dir = /[֐-׿؀-ۿ]/.test(of) ? 'rtl' : 'ltr';
    back.disabled = !live || page.index === 0;
    const last = page.index >= page.count - 1;
    // The asset's rule binds only a provider that navigates page by page; free navigation ignores it.
    const locked = navigation === 'linear' && page.canAdvance === false;
    next.disabled = !live || last || locked;
    next.title = locked ? 'Answer this page to continue' : last ? 'This is the last page' : '';

    // Dots only while they still read as dots; past that the number says it better.
    pageDots.hidden = page.count > 20;
    pageDots.replaceChildren(
      ...Array.from({ length: page.count }, (_, index) => {
        const dot = document.createElement('span');
        dot.dataset['state'] = index === page.index ? 'current' : index < page.index ? 'seen' : 'ahead';
        return dot;
      }),
    );
  }
}
